import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/helpers';
import { toArticlesApiError } from '../api/articlesApi';
import type { Article, CreateArticleInput, UpdateArticleInput } from '../types';

export type ArticlePanelMode = 'view' | 'create' | 'edit';

interface ArticlePanelProps {
  open: boolean;
  mode: ArticlePanelMode;
  article: Article | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: CreateArticleInput | UpdateArticleInput) => Promise<void>;
}

interface ArticleFormValues {
  title: string;
  content: string;
}

type FieldErrors = Partial<Record<keyof ArticleFormValues, string>>;

const EMPTY_VALUES: ArticleFormValues = { title: '', content: '' };

const safeFormatDate = (value: string | null): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const ArticlePanel: React.FC<ArticlePanelProps> = ({
  open,
  mode,
  article,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<ArticleFormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if (!open) return;
    setValues(article ? { title: article.title, content: article.content } : EMPTY_VALUES);
    setFieldErrors({});
    setServerError('');
  }, [article, open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose, open]);

  if (!open || (mode !== 'create' && !article)) return null;

  const setField = (field: keyof ArticleFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!values.title.trim()) errors.title = t('admin.articles.validation.title_required');
    else if (values.title.length > 200) errors.title = t('admin.articles.validation.title_max');
    if (!values.content.trim()) errors.content = t('admin.articles.validation.content_required');
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isReadOnly) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setServerError('');
    try {
      await onSubmit({ title: values.title.trim(), content: values.content.trim() });
      onClose();
    } catch (error: unknown) {
      const normalized = toArticlesApiError(error);
      setServerError(normalized.message);
      setFieldErrors(Object.fromEntries(
        Object.entries(normalized.fieldErrors ?? {}).map(([field, messages]) => [field, messages[0]]),
      ) as FieldErrors);
    }
  };

  const panelTitle = mode === 'create'
    ? t('admin.articles.panel.create_title')
    : mode === 'edit'
      ? t('admin.articles.panel.edit_title')
      : t('admin.articles.panel.view_title');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-0 sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="flex h-full w-full flex-col overflow-hidden bg-white shadow-xl sm:max-h-[92vh] sm:max-w-[900px] sm:rounded-[24px] sm:border sm:border-outline-variant"
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-panel-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id="article-panel-title" className="font-h2 text-h2 text-on-surface">{panelTitle}</h2>
            <p className="mt-1 text-body-sm text-secondary">{t('admin.articles.panel.description')}</p>
          </div>
          <button type="button" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary hover:bg-surface-container-low disabled:opacity-50" disabled={isSubmitting} aria-label={t('admin.articles.actions.close')} onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <form id="article-form" className="flex-1 overflow-y-auto px-4 py-5 sm:px-6" onSubmit={handleSubmit}>
          {serverError && (
            <div className="mb-5 flex items-start gap-2 rounded-xl border border-error/30 bg-error-container/40 px-3 py-2 text-body-sm text-error" role="alert">
              <span className="material-symbols-outlined text-[19px]">error</span>
              <span>{serverError}</span>
            </div>
          )}

          {isReadOnly && article ? (
            <article className="mx-auto max-w-[760px]">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-body-sm text-secondary">
                <span>#{article.id}</span>
                <span className="inline-flex items-center gap-1"><span className="material-symbols-outlined text-[17px]">calendar_today</span>{safeFormatDate(article.createAt)}</span>
                <span className="inline-flex items-center gap-1"><span className="material-symbols-outlined text-[17px]">update</span>{safeFormatDate(article.updateAt)}</span>
              </div>
              <h3 className="mt-5 text-[28px] font-bold leading-9 text-on-surface sm:text-[34px] sm:leading-[44px]">{article.title}</h3>
              <div className="mt-6 whitespace-pre-wrap border-t border-outline-variant pt-6 font-body-lg text-body-lg leading-7 text-on-surface">{article.content}</div>
            </article>
          ) : (
            <div className="mx-auto flex max-w-[800px] flex-col gap-5">
              <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
                {t('admin.articles.fields.title')} <span className="text-error">*</span>
                <input
                  className="h-11 w-full rounded-xl border border-outline-variant bg-white px-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container-low"
                  value={values.title}
                  maxLength={201}
                  disabled={isSubmitting}
                  autoFocus
                  onChange={(event) => setField('title', event.target.value)}
                />
                <span className={`text-right text-body-sm font-normal ${values.title.length > 200 ? 'text-error' : 'text-secondary'}`}>{values.title.length}/200</span>
                {fieldErrors.title && <span className="text-body-sm font-normal text-error">{fieldErrors.title}</span>}
              </label>

              <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
                {t('admin.articles.fields.content')} <span className="text-error">*</span>
                <textarea
                  className="min-h-[340px] w-full resize-y rounded-xl border border-outline-variant bg-white px-4 py-3 font-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container-low sm:min-h-[400px]"
                  value={values.content}
                  disabled={isSubmitting}
                  placeholder={t('admin.articles.fields.content_placeholder')}
                  onChange={(event) => setField('content', event.target.value)}
                />
                {fieldErrors.content && <span className="text-body-sm font-normal text-error">{fieldErrors.content}</span>}
              </label>
            </div>
          )}
        </form>

        <footer className="flex flex-col-reverse gap-2 border-t border-outline-variant bg-surface-container-lowest px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <button type="button" className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-50" disabled={isSubmitting} onClick={onClose}>
            {t(isReadOnly ? 'admin.articles.actions.close' : 'admin.articles.actions.cancel')}
          </button>
          {!isReadOnly && (
            <button type="submit" form="article-form" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary px-5 font-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:opacity-60" disabled={isSubmitting}>
              {isSubmitting && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
              {t(isSubmitting ? 'admin.articles.actions.saving' : 'admin.articles.actions.save')}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
};

export default ArticlePanel;
